import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { regions } from '@/mocks/regions';
import {
  PLANS,
  PLAN_LABELS,
  formatPrice,
  BANK_INFO,
  TRANSFER_NOTICE,
  APPROVAL_NOTICE,
  REFUND_POLICIES,
  type PlanKey,
} from '@/lib/plans';

type Mode = 'login' | 'signup';
type Role = 'member' | 'owner';

const planKeys: PlanKey[] = ['1month', '3month', '12month'];

export default function Login() {
  const navigate = useNavigate();
  const [mode, setMode] = useState<Mode>('login');

  return (
    <div className="min-h-screen bg-background-50 flex items-center justify-center px-4 py-10">
      <div className="w-full max-w-md">
        <button
          onClick={() => navigate('/')}
          className="flex items-center gap-2 text-foreground-600 hover:text-foreground-900 text-sm mb-6 transition-colors cursor-pointer"
        >
          <i className="ri-arrow-left-line"></i>
          홈으로 돌아가기
        </button>

        <div className="bg-white rounded-2xl border border-background-200 p-6 md:p-8">
          <div className="text-center mb-8">
            <div className="w-12 h-12 mx-auto flex items-center justify-center rounded-xl bg-primary-100 text-primary-700 mb-4">
              <i className="ri-graduation-cap-line text-2xl"></i>
            </div>
            <h1 className="font-heading text-2xl font-bold text-foreground-950">
              MC에듀
            </h1>
            <p className="text-foreground-500 text-sm mt-2">
              {mode === 'login' ? '계정으로 로그인하세요' : '계정을 만들어 진로탐구를 시작하세요'}
            </p>
          </div>

          {mode === 'login' ? (
            <LoginForm navigate={navigate} />
          ) : (
            <SignupForm navigate={navigate} />
          )}

          <div className="mt-6 text-center text-sm text-foreground-500">
            {mode === 'login' ? (
              <span>
                아직 계정이 없으신가요?{' '}
                <button
                  onClick={() => setMode('signup')}
                  className="text-primary-600 font-semibold hover:text-primary-700 cursor-pointer"
                >
                  회원가입
                </button>
              </span>
            ) : (
              <span>
                이미 계정이 있으신가요?{' '}
                <button
                  onClick={() => setMode('login')}
                  className="text-primary-600 font-semibold hover:text-primary-700 cursor-pointer"
                >
                  로그인
                </button>
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function LoginForm({ navigate }: { navigate: (path: string) => void }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    // 이메일이 아닌 학생 아이디 입력 시 내부 이메일로 변환
    let loginEmail = email.trim();
    if (loginEmail && !loginEmail.includes('@')) {
      loginEmail = `${loginEmail.toLowerCase()}@student.mcedu.app`;
    }
    const { error: err } = await supabase.auth.signInWithPassword({
      email: loginEmail,
      password,
    });
    setLoading(false);
    if (err) {
      setError('아이디 또는 비밀번호가 올바르지 않습니다.');
      return;
    }
    navigate('/dashboard');
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <Field label="이메일 또는 학생 아이디">
        <input
          type="text"
          name="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="w-full px-4 py-2.5 text-sm rounded-md border border-background-300 bg-background-50 focus:outline-none focus:ring-2 focus:ring-primary-300"
          placeholder="이메일 또는 학원에서 받은 아이디"
        />
      </Field>
      <Field label="비밀번호">
        <input
          type="password"
          name="password"
          required
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          className="w-full px-4 py-2.5 text-sm rounded-md border border-background-300 bg-background-50 focus:outline-none focus:ring-2 focus:ring-primary-300"
          placeholder="비밀번호 입력"
        />
      </Field>

      <div className="flex justify-end">
        <button
          type="button"
          onClick={() => navigate('/find-account')}
          className="text-sm text-foreground-500 hover:text-primary-600 transition-colors cursor-pointer"
        >
          아이디/비밀번호 찾기
        </button>
      </div>

      {error && (
        <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2">
          {error}
        </div>
      )}

      <button
        type="submit"
        disabled={loading}
        className="w-full whitespace-nowrap bg-primary-500 hover:bg-primary-600 text-white font-semibold py-3 rounded-md transition-colors cursor-pointer disabled:opacity-60 text-sm"
      >
        {loading ? '로그인 중...' : '로그인'}
      </button>
    </form>
  );
}

function SignupForm({ navigate }: { navigate: (path: string) => void }) {
  const [role, setRole] = useState<Role>('member');
  const [plan, setPlan] = useState<PlanKey>('1month');
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [city, setCity] = useState('');
  const [district, setDistrict] = useState('');
  const [academyName, setAcademyName] = useState('');
  const [phone, setPhone] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [capacityMsg, setCapacityMsg] = useState('');
  const [loading, setLoading] = useState(false);

  const selectedRegion = regions.find((r) => r.city === city);
  const needsRegion = role === 'owner';
  const needsAcademyName = role === 'owner';
  const needsPlan = role === 'member' || role === 'owner';

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setCapacityMsg('');

    if (password.length < 6) {
      setError('비밀번호는 6자 이상이어야 합니다.');
      return;
    }
    if (password !== passwordConfirm) {
      setError('비밀번호가 일치하지 않습니다.');
      return;
    }
    if (needsRegion && (!city || !district)) {
      setError('지역(시/도, 구/군)을 선택해주세요.');
      return;
    }
    if (!phone.trim()) {
      setError('휴대폰 번호를 입력해주세요.');
      return;
    }
    if (!/^[0-9]{10,11}$/.test(phone.replace(/[^0-9]/g, ''))) {
      setError('올바른 휴대폰 번호를 입력해주세요.');
      return;
    }

    setLoading(true);
    const { data, error: err } = await supabase.functions.invoke('signup', {
      body: {
        email,
        password,
        metadata: {
          full_name: fullName,
          role,
          plan: needsPlan ? plan : null,
          academy_name: academyName,
          city,
          district,
          address: needsRegion ? `${city} ${district}` : null,
          phone,
        },
      },
    });
    setLoading(false);

    if (err) {
      // 엣지함수가 4xx/5xx로 응답하면 supabase-js는 data 대신 error로 감싸서 돌려줘요.
      // 실제 서버 메시지(이미 가입된 이메일, 가입 인원 초과 등)를 꺼내서 보여줍니다.
      let serverData: {
        success?: boolean;
        message?: string;
        capacityFull?: boolean;
        alreadyExists?: boolean;
      } | null = null;
      try {
        const context = (err as { context?: { json: () => Promise<unknown> } })
          .context;
        if (context && typeof context.json === 'function') {
          serverData = (await context.json()) as typeof serverData;
        }
      } catch {
        serverData = null;
      }

      if (serverData?.capacityFull) {
        setCapacityMsg(
          serverData.message ||
            '죄송합니다. 가입 허용 인원이 모두 차서 더 이상 가입하실 수 없습니다.',
        );
      } else if (serverData?.alreadyExists) {
        setError('중복된 아이디입니다.');
      } else if (serverData?.message) {
        setError(serverData.message);
      } else {
        setError('회원가입 중 오류가 발생했습니다. 잠시 후 다시 시도해주세요.');
      }
      return;
    }

    if (!data?.success) {
      if (data?.capacityFull) {
        setCapacityMsg(
          data?.message ||
            '죄송합니다. 가입 허용 인원이 모두 차서 더 이상 가입하실 수 없습니다.',
        );
      } else if (data?.alreadyExists) {
        setError('중복된 아이디입니다.');
      } else {
        setError(data?.message || '회원가입 중 오류가 발생했습니다.');
      }
      return;
    }

    setSuccess('가입 신청이 완료되었습니다! 아래 계좌로 입금하시면 운영자가 확인 후 이용권을 활성화해드립니다.');
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* 역할 선택 */}
      <div>
        <span className="block text-sm font-medium text-foreground-700 mb-1.5">
          가입 유형
        </span>
        <div className="grid grid-cols-2 gap-2 bg-background-100 p-1 rounded-full">
          <button
            type="button"
            onClick={() => setRole('member')}
            className={`whitespace-nowrap py-2 rounded-full text-xs sm:text-sm font-semibold transition-colors cursor-pointer ${
              role === 'member'
                ? 'bg-white text-foreground-950 border border-background-300'
                : 'text-foreground-500 hover:text-foreground-700'
            }`}
          >
            <i className="ri-user-star-line mr-1"></i>일반회원
          </button>
          <button
            type="button"
            onClick={() => setRole('owner')}
            className={`whitespace-nowrap py-2 rounded-full text-xs sm:text-sm font-semibold transition-colors cursor-pointer ${
              role === 'owner'
                ? 'bg-white text-foreground-950 border border-background-300'
                : 'text-foreground-500 hover:text-foreground-700'
            }`}
          >
            <i className="ri-building-2-line mr-1"></i>학원장
          </button>
        </div>
        <p className="text-xs text-foreground-500 mt-1.5">
          {role === 'member' && '개인 이용자 (유료 이용권)'}
          {role === 'owner' && '학원 운영자 (학원 단위 유료 이용권)'}
        </p>
        <p className="text-[11px] text-foreground-400 mt-1">
          개인회원 최대 2,000명 · 학원장 최대 1,000명까지 가입 가능합니다.
        </p>
      </div>

      <Field label="이름">
        <input
          type="text"
          name="full_name"
          required
          value={fullName}
          onChange={(e) => setFullName(e.target.value)}
          className="w-full px-4 py-2.5 text-sm rounded-md border border-background-300 bg-background-50 focus:outline-none focus:ring-2 focus:ring-primary-300"
          placeholder={role === 'owner' ? '원장님 성함' : '이름'}
        />
      </Field>
      <Field label="이메일">
        <input
          type="email"
          name="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="w-full px-4 py-2.5 text-sm rounded-md border border-background-300 bg-background-50 focus:outline-none focus:ring-2 focus:ring-primary-300"
          placeholder="name@example.com"
        />
      </Field>
      <Field label="휴대폰 번호">
        <input
          type="tel"
          name="phone"
          required
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          className="w-full px-4 py-2.5 text-sm rounded-md border border-background-300 bg-background-50 focus:outline-none focus:ring-2 focus:ring-primary-300"
          placeholder="010-1234-5678"
        />
      </Field>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field label="비밀번호">
          <input
            type="password"
            name="password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full px-4 py-2.5 text-sm rounded-md border border-background-300 bg-background-50 focus:outline-none focus:ring-2 focus:ring-primary-300"
            placeholder="6자 이상"
          />
        </Field>
        <Field label="비밀번호 확인">
          <input
            type="password"
            name="password_confirm"
            required
            value={passwordConfirm}
            onChange={(e) => setPasswordConfirm(e.target.value)}
            className="w-full px-4 py-2.5 text-sm rounded-md border border-background-300 bg-background-50 focus:outline-none focus:ring-2 focus:ring-primary-300"
            placeholder="다시 입력"
          />
        </Field>
      </div>

      {/* 지역 선택 (학생/원장) */}
      {needsRegion && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="시/도">
            <select
              name="city"
              required
              value={city}
              onChange={(e) => {
                setCity(e.target.value);
                setDistrict('');
              }}
              className="w-full px-3 py-2.5 text-sm rounded-md border border-background-300 bg-background-50 focus:outline-none focus:ring-2 focus:ring-primary-300 cursor-pointer"
            >
              <option value="">시/도 선택</option>
              {regions.map((r) => (
                <option key={r.city} value={r.city}>
                  {r.city}
                </option>
              ))}
            </select>
          </Field>
          <Field label="구/군">
            <select
              name="district"
              required
              value={district}
              onChange={(e) => setDistrict(e.target.value)}
              disabled={!city}
              className="w-full px-3 py-2.5 text-sm rounded-md border border-background-300 bg-background-50 focus:outline-none focus:ring-2 focus:ring-primary-300 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <option value="">구/군 선택</option>
              {selectedRegion?.districts.map((d) => (
                <option key={d} value={d}>
                  {d}
                </option>
              ))}
            </select>
          </Field>
        </div>
      )}

      {needsAcademyName && (
        <Field label="학원명">
          <input
            type="text"
            name="academy_name"
            required
            value={academyName}
            onChange={(e) => setAcademyName(e.target.value)}
            className="w-full px-4 py-2.5 text-sm rounded-md border border-background-300 bg-background-50 focus:outline-none focus:ring-2 focus:ring-primary-300"
            placeholder="운영 중인 학원 이름"
          />
        </Field>
      )}

      {/* 이용권 선택 (일반회원/학원장) */}
      {needsPlan && (
        <div>
          <span className="block text-sm font-medium text-foreground-700 mb-1.5">
            이용권 선택
          </span>
          <div className="grid grid-cols-3 gap-2">
            {planKeys.map((key) => {
              const selected = plan === key;
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => setPlan(key)}
                  className={`whitespace-nowrap rounded-lg border p-3 text-center transition-colors cursor-pointer ${
                    selected
                      ? 'border-primary-500 bg-primary-50'
                      : 'border-background-200 bg-white hover:border-background-300'
                  }`}
                >
                  <div className="text-sm font-semibold text-foreground-950">
                    {PLAN_LABELS[key]}
                  </div>
                  <div className="text-xs text-primary-700 font-semibold mt-1">
                    {formatPrice(PLANS[role][key])}원
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {needsPlan && (
        <div className="rounded-md bg-accent-50 border border-accent-200 px-3 py-2.5 text-xs text-foreground-700">
          <div className="flex items-center gap-1.5 font-semibold text-foreground-900">
            <i className="ri-bank-card-line"></i>
            입금 계좌 안내
          </div>
          <div className="mt-1">
            {BANK_INFO.bank} {BANK_INFO.account} · {BANK_INFO.holder}
          </div>
          <div className="mt-2 pt-2 border-t border-accent-200 text-foreground-700 flex items-start gap-1.5">
            <i className="ri-alert-line mt-0.5 text-accent-700 flex-shrink-0"></i>
            <span>{TRANSFER_NOTICE}</span>
          </div>
        </div>
      )}

      {needsPlan && (
        <div className="rounded-md bg-background-100 border border-background-200 px-3 py-2.5 text-xs text-foreground-600">
          <div className="font-semibold text-foreground-800 mb-1.5">환불정책 안내</div>
          <ul className="space-y-1">
            {REFUND_POLICIES.map((policy) => (
              <li key={policy} className="flex items-start gap-1.5">
                <i className="ri-checkbox-circle-line mt-0.5 text-foreground-400 flex-shrink-0"></i>
                <span>{policy}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {capacityMsg && (
        <div className="rounded-md bg-amber-50 border border-amber-200 px-4 py-3 text-sm text-amber-800">
          <div className="flex items-start gap-2">
            <i className="ri-information-line mt-0.5 text-amber-600 flex-shrink-0"></i>
            <div>
              <p className="font-semibold text-amber-900">가입 인원 초과 안내</p>
              <p className="mt-1">{capacityMsg}</p>
            </div>
          </div>
        </div>
      )}
      {error && (
        <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2">
          {error}
        </div>
      )}
      {success && (
        <div className="text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-md px-3 py-3 space-y-2">
          <p className="font-semibold">{success}</p>
          <p className="text-xs text-emerald-600/90 flex items-center gap-1">
            <i className="ri-time-line"></i>
            {APPROVAL_NOTICE}
          </p>
          {needsPlan && (
            <div className="rounded-md bg-white border border-emerald-200 px-3 py-2 text-xs">
              <div className="font-semibold text-foreground-900 mb-1">입금 계좌</div>
              <div className="text-emerald-700 font-semibold">
                {BANK_INFO.bank} {BANK_INFO.account}
              </div>
              <div className="text-foreground-600">예금주 {BANK_INFO.holder}</div>
              <div className="text-foreground-500 mt-1">
                신청 이용권: {PLAN_LABELS[plan]} · {formatPrice(PLANS[role][plan])}원
              </div>
            </div>
          )}
          <button
            type="button"
            onClick={() => navigate('/')}
            className="w-full whitespace-nowrap bg-emerald-600 hover:bg-emerald-700 text-white font-semibold py-2 rounded-md transition-colors cursor-pointer text-sm"
          >
            <i className="ri-home-5-line mr-1"></i>
            홈으로 돌아가기
          </button>
        </div>
      )}

      <button
        type="submit"
        disabled={loading}
        className="w-full whitespace-nowrap bg-primary-500 hover:bg-primary-600 text-white font-semibold py-3 rounded-md transition-colors cursor-pointer disabled:opacity-60 text-sm"
      >
        {loading ? '가입 중...' : '회원가입'}
      </button>
    </form>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="block text-sm font-medium text-foreground-700 mb-1.5">{label}</span>
      {children}
    </label>
  );
}